import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";
import { LoopVideo } from "@/components/site/LoopVideo";
import { CheckCircle2 } from "lucide-react";

const SECTIONS = [
  ["what_is_it", "What is it?"],
  ["why_recommended", "Why might it be recommended?"],
  ["what_it_feels_like", "What does treatment feel like?"],
  ["role_in_approach", "Its role in the Psalm 30:2 approach"],
  ["who_is_it_for", "Who might be appropriate for it?"],
  ["what_to_expect", "What should you expect?"],
  ["limitations", "Reasonable limitations & considerations"],
  ["integration", "How it integrates with other care"],
];

function Benefits({ s }) {
  return (
    <section className="bg-espresso text-ivory py-20 lg:py-28 px-6 md:px-10 lg:px-14" data-testid="treatment-benefits">
      <div className="max-w-[1200px] mx-auto grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <div className="overline !text-brass mb-5">During each treatment</div>
          <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.05]">{s.benefits_title}</h2>
          {s.benefits_closing && <p className="mt-8 font-serif italic text-2xl text-brass/90 leading-snug">{s.benefits_closing}</p>}
        </Reveal>
        <ul className="lg:col-span-7 space-y-5">
          {s.benefits.map((b, i) => (
            <Reveal as="li" key={b} delay={i * 60} className="flex gap-4 border-b border-ivory/10 pb-5">
              <CheckCircle2 size={22} className="text-brass shrink-0 mt-0.5" strokeWidth={1.4} />
              <span className="font-light text-lg leading-relaxed text-ivory/85" data-testid={`treatment-benefit-${i}`}>{b}</span>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

function VideoHero({ s }) {
  return (
    <section className="relative h-[82vh] min-h-[560px] overflow-hidden bg-espresso" data-testid="treatment-video-hero">
      <LoopVideo src={resolveImg(s.hero_video)} poster={resolveImg(s.hero_poster || s.image)} label={`${s.name} treatment at Psalm 30:2`}
        className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: s.hero_focus || "center" }} testid="treatment-hero-video" />
      <div className="absolute inset-0 bg-gradient-to-t from-espresso/85 via-espresso/35 to-espresso/40" />
      <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-16 lg:pb-24 pt-32">
        <Link to="/treatments" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ivory/70 hover:text-ivory mb-8 w-fit" data-testid="treatment-back">
          <ArrowLeft size={15} /> All Treatments
        </Link>
        <div className="overline !text-brass mb-5">{s.category}</div>
        <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.98] text-ivory max-w-4xl">{s.name}</h1>
        <p className="mt-6 text-lg lg:text-xl font-light text-ivory/80 max-w-xl">{s.tagline}</p>
      </div>
    </section>
  );
}

export default function TreatmentDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setS(null);
    api.get(`/services/${slug}`).then((r) => setS(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="pt-48 text-center pb-40"><h1 className="font-serif text-4xl">Treatment not found</h1><Link to="/treatments" className="link-underline mt-4 inline-block">Back to treatments</Link></div>;
  if (!s) return <div className="h-screen" />;

  return (
    <div>
      <Seo title={`${s.name} in Gardner, KS | Psalm 30:2 Chiropractic`} description={s.tagline} />
      {s.hero_video ? <VideoHero s={s} /> : (
      <section className="pt-36 lg:pt-44 pb-14 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto">
          <Link to="/treatments" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-taupe hover:text-espresso mb-10" data-testid="treatment-back">
            <ArrowLeft size={15} /> All Treatments
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="overline mb-5">{s.category}</div>
              <h1 className="font-serif font-light text-5xl lg:text-7xl leading-[1.0] text-espresso">{s.name}</h1>
              <p className="mt-6 text-xl font-light text-espressosoft/80 max-w-lg">{s.tagline}</p>
            </div>
          </div>
          <div className="mt-12 overflow-hidden rounded-sm aspect-[16/8]">
            <img src={resolveImg(s.image)} alt={s.name} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
      )}

      {s.benefits?.length > 0 && <Benefits s={s} />}

      <section className="bg-ivory pb-8 px-6 md:px-10 lg:px-14">
        <div className="max-w-3xl mx-auto space-y-14 py-8">
          {SECTIONS.map(([key, label]) =>
            s[key] ? (
              <Reveal key={key}>
                <div className="grid md:grid-cols-3 gap-6 border-t border-espresso/10 pt-8">
                  <h2 className="md:col-span-1 font-serif text-2xl text-espresso">{label}</h2>
                  <div className="md:col-span-2 space-y-4 text-lg font-light leading-relaxed text-espressosoft/85" data-testid={`treatment-section-${key}`}>
                    {s[key].split(/\n\s*\n/).map((para) => <p key={para.slice(0, 40)}>{para}</p>)}
                  </div>
                </div>
              </Reveal>
            ) : null
          )}
        </div>
      </section>

      <section className="bg-linen py-20 px-6 text-center" data-testid="treatment-cta">
        <Reveal>
          <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso mb-3">Ready to begin?</h2>
          <p className="text-espressosoft/70 font-light mb-8">We'll assess whether this may be appropriate for you.</p>
          <BookButton label="Book a Visit" variant="primary" track_label={`treatment-${s.slug}`} testid="treatment-book-button" />
        </Reveal>
        <p className="mt-10 text-xs text-taupe max-w-xl mx-auto">This information is educational and not a medical promise or diagnosis. Suitability is always determined through individual assessment.</p>
      </section>
    </div>
  );
}
